import React, { useState, useEffect, useMemo, useRef } from 'react';
import config from '../../config';
import '../../assets/css/Snooker.css';
import '../../assets/css/SnookerThemes.css';
import { normalizeScoreboardTheme } from '../../constants/scoreboardThemes';
import ScoreboardFrame from '../../Components/ScoreboardFrame';

const PLAYER_NAME_SINGLE_LINE_LIMIT = 14;

const normalizePlayerName = name => String(name || '').trim().replace(/\s+/g, ' ');

export const shouldWrapPlayerNames = (...names) => names.some(name => (
    normalizePlayerName(name).length > PLAYER_NAME_SINGLE_LINE_LIMIT
));

export const formatPlayerName = (name, wrapBothNames) => {
    const normalized = normalizePlayerName(name);
    if (!wrapBothNames) return normalized;
    const parts = normalized.split(' ').filter(Boolean);
    if (parts.length < 2) return normalized;
    return `${parts[0]}\n${parts.slice(1).join(' ')}`;
};

const SnookerShotTimer = ({ calculateShotRemaining, maxShotTime }) => {
    const [remainingSec, setRemainingSec] = useState(0);
    const [progress, setProgress] = useState(100);
    const calculateShotRemainingRef = useRef(calculateShotRemaining);
    const maxShotTimeRef = useRef(maxShotTime);

    useEffect(() => {
        calculateShotRemainingRef.current = calculateShotRemaining;
        maxShotTimeRef.current = maxShotTime;
    });

    useEffect(() => {
        const update = () => {
            const remainingMs = calculateShotRemainingRef.current();
            const sec = Math.max(0, Math.ceil(remainingMs / 1000));
            const prog = Math.max(0, Math.min(100, (remainingMs / (maxShotTimeRef.current * 1000)) * 100));
            setRemainingSec(sec);
            setProgress(prog);
        };
        update();
        const intervalId = setInterval(update, 100);
        return () => clearInterval(intervalId);
    }, []);
    return (
        <div className="shootout-timer" aria-live="polite" title={`Shot timer: ${remainingSec}s / ${maxShotTime}s`}>
            <div className={`shootout-display ${remainingSec <= 5 ? 'warning' : 'ok'}`}>
                <div className="shootout-digits">
                    {String(remainingSec).padStart(2, '0')}
                    <span className="shootout-suffix">s</span>
                </div>
                <div className="shootout-bar">
                    <div className="shootout-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <div className="shootout-max">/{maxShotTime}s</div>
            </div>
        </div>
    );
};
const Snooker = React.memo(({ scoreboardTheme = 'modern-dark', gameState, game, showControls, setShowControls, user, displayTime, message, winMessage, frameBreaks, actionStack, potRed, potColor, switchPlayer, freeBall, addFoul, winFrame, undoAction, resetFrames, startTimer, pauseTimer, resetTimer, getFlagComponent, formatShotTime, settings, calculateShotRemaining, startShotTimer, stopShotTimer, toggleFullScreen, resetShotTimer, calculateCurrentTime }) => {
    const frameElapsed = calculateCurrentTime();
    const remainingTimeMs = calculateShotRemaining();
    const activeLimitMs = Number.isFinite(gameState.currentShotLimitMs) && gameState.currentShotLimitMs > 0
        ? gameState.currentShotLimitMs
        : (frameElapsed >= 300000 ? 10000 : 15000);
    const remainingTimeSec = Math.max(0, Math.ceil(remainingTimeMs / 1000));
    const maxShotTime = Math.round(activeLimitMs / 1000);
    const maxShotMs = activeLimitMs;
    const progressWidth = Math.max(0, Math.min(100, (remainingTimeSec / maxShotTime) * 100));
    // Check if shot timer is enabled for snooker
    const isShotTimerEnabled = settings?.snooker_shot_timer_enabled === 'true';
    // Determine globally last potted ball action
    const lastPotAction = useMemo(() => {
        return [...(actionStack || [])].reverse().find(a => a?.action === 'potRed' || a?.action === 'potColor');
    }, [actionStack]);
    const lastPotPlayer = lastPotAction?.player;
    const namesNeedTwoLines = useMemo(
        () => shouldWrapPlayerNames(gameState.firstPlayer, gameState.secondPlayer),
        [gameState.firstPlayer, gameState.secondPlayer]
    );
    return (
        <div className="snooker-wrapper" data-scoreboard-theme={normalizeScoreboardTheme(scoreboardTheme)}>
            <ScoreboardFrame variant={scoreboardTheme === 'classic-green' ? scoreboardTheme : null} />
            <div className="scoreboard-container">
                <ScoreboardFrame variant={scoreboardTheme === 'premium' ? scoreboardTheme : null} />
                {gameState.isGameFinished && winMessage && (
                    <div
                        className="finished-cnt"
                        style={{ position: 'absolute', top: 0, left: 0, backgroundColor: 'rgba(0, 0, 0, .75)', width: '100%', height: '100%', borderRadius: '1.5vw', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: '#fff', fontSize: '6vw', textAlign: 'center', zIndex: 99999999, pointerEvents: 'none' }}
                    >
                        <p style={{ margin: 0 }}>{winMessage}</p>
                        {/* Pinned to the bottom rather than stacked under the message, so it
                            reads as a footnote and does not shift the message off centre. */}
                        <p style={{ position: 'absolute', bottom: '2vw', left: 0, right: 0, margin: 0, fontSize: '1.6vw', opacity: 0.8 }}>Press + to reset the frames</p>
                    </div>
                )}
                <div className="header">
                    <div className="table-info" style={{ width: '18vw', textAlign: 'center' }}>
                        <ScoreboardFrame variant={scoreboardTheme === 'premium' ? scoreboardTheme : null} detail="panel" />
                        {game ? game.name : 'TABLE 1'}
                    </div>
                    {showControls && (
                        <div className="header-controls">
                            <div className="action-controls">
                                <button className="control-btn" disabled={gameState.isGameFinished} onClick={switchPlayer}>Switch</button>
                                <button className="control-btn" disabled={gameState.frameOver || gameState.isGameFinished} onClick={freeBall}>Free Ball</button>
                                <button className="control-btn" disabled={gameState.frameOver || gameState.isGameFinished} onClick={addFoul}>Foul</button>
                                <button className="control-btn" disabled={gameState.isGameFinished} onClick={winFrame}>Win</button>
                                <button className="control-btn" disabled={actionStack.length === 0 && !gameState.selectingFoulValue && !gameState.freeBallActive} onClick={undoAction}>Undo</button>
                                <button className="control-btn" onClick={resetFrames}>Reset Frames</button>
                            </div>
                            <div className="timer-controls">
                                <button className="control-btn" disabled={gameState.isGameFinished || gameState.frameOver} onClick={startTimer}>Start</button>
                                <button className="control-btn" disabled={gameState.isGameFinished} onClick={pauseTimer}>Pause</button>
                                <button className="control-btn" disabled={gameState.isGameFinished} onClick={resetTimer}>Reset</button>
                                {isShotTimerEnabled && (
                                    <>
                                        <button className="control-btn" disabled={gameState.isGameFinished || gameState.frameOver} onClick={startShotTimer}>Start Shot</button>
                                        <button className="control-btn" disabled={gameState.isGameFinished} onClick={stopShotTimer}>Stop Shot</button>
                                        <button className="control-btn" disabled={gameState.isGameFinished} onClick={resetShotTimer}>Reset Shot</button>
                                    </>
                                )}
                                <button className="control-btn" disabled={gameState.isGameFinished} onClick={() => { toggleFullScreen(); setShowControls(false); }}>Fullscreen</button>
                            </div>
                        </div>
                    )}
                    {!showControls && user && user.logo && user.logo !== 'null' && (
                        <img src={`${config.STORAGE_BASE_URL}/${user.logo}`} alt="Club logo" className="club-logo" />
                    )}
                    <div className="timer" style={{ width: '18vw', textAlign: 'center' }}>
                        <ScoreboardFrame variant={scoreboardTheme === 'premium' ? scoreboardTheme : null} detail="panel" />
                        {displayTime}
                    </div>
                </div>
                {/* Foul/free-ball decisions belong on the referee page, not the spectator display. */}
                <div className={`score-section ${namesNeedTwoLines ? 'score-section--two-line-names' : 'score-section--single-line-names'}`}>
                    <div className="player-info">
                        {game && game.has_flags != 0 && (
                            getFlagComponent(gameState.firstPlayerNationality)
                        )}
                        <div className={`player-name ${namesNeedTwoLines ? 'player-name--two-line' : 'player-name--single-line'}`}>
                            {formatPlayerName(gameState.firstPlayer, namesNeedTwoLines)}
                        </div>
                        <div className="frame-score">{gameState.player1FramesWon}</div>
                    </div>
                    <div className="frames-info">
                        <div
                            className="frames-label clickable"
                            onClick={() => setShowControls(s => !s)}
                            title={showControls ? 'Hide controls' : 'Show controls'}
                        >
                            FRAMES
                        </div>
                        {(game && (game.is_race == 1 || game.is_race === '1')) && (
                            <div className="frames-total">({(parseInt(game.race_to) * 2) - 1})</div>
                        )}
                    </div>
                    <div className="player-info" style={{ flexDirection: 'row-reverse' }}>
                        {game && game.has_flags != 0 && (
                            getFlagComponent(gameState.secondPlayerNationality)
                        )}
                        <div className={`player-name ${namesNeedTwoLines ? 'player-name--two-line' : 'player-name--single-line'}`} style={{ textAlign: 'right' }}>
                            {formatPlayerName(gameState.secondPlayer, namesNeedTwoLines)}
                        </div>
                        <div className="frame-score">{gameState.player2FramesWon}</div>
                    </div>
                </div>
                <div className="score-container">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                        <div className={`score-box ${gameState.currentPlayer === 1 ? 'active' : ''}`}>
                            <ScoreboardFrame variant={scoreboardTheme === 'premium' ? scoreboardTheme : null} detail="panel" />
                            <div className="score-number">{gameState.player1Score}</div>
                        </div>
                        {isShotTimerEnabled && (gameState.timerOwner === 1 || (!gameState.timerOwner && gameState.currentPlayer === 1)) && (
                            <SnookerShotTimer calculateShotRemaining={calculateShotRemaining} maxShotTime={maxShotTime} />
                        )}
                    </div>
                    <div className="score-label">SCORE</div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                        <div className={`score-box ${gameState.currentPlayer === 2 ? 'active' : ''}`}>
                            <ScoreboardFrame variant={scoreboardTheme === 'premium' ? scoreboardTheme : null} detail="panel" />
                            <div className="score-number">{gameState.player2Score}</div>
                        </div>
                        {isShotTimerEnabled && (gameState.timerOwner === 2 || (!gameState.timerOwner && gameState.currentPlayer === 2)) && (
                            <SnookerShotTimer calculateShotRemaining={calculateShotRemaining} maxShotTime={maxShotTime} />
                        )}
                    </div>
                </div>
                {isShotTimerEnabled ? null : (
                    <div className={`break-section ${gameState.currentBreakPoints > 0 ? 'show' : ''}`} aria-hidden={gameState.currentBreakPoints === 0}>
                        <div className="break-display">
                            <span className="break-label">BREAK:</span>
                            <span className="break-value">{gameState.currentBreakPoints}</span>
                            <div className="balls-container">
                                {['red', 'yellow', 'green', 'brown', 'blue', 'pink', 'black'].map((color, index) =>
                                    gameState.currentBreakPottedBalls.filter(b => b.toLowerCase() === color).length > 0 ? (
                                        <div key={`break-${index}`} className={`ball ${color}`}>
                                            {gameState.currentBreakPottedBalls.filter(b => b.toLowerCase() === color).length}
                                        </div>
                                    ) : null
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="bottom-fixed">
                    <div className="remaining">REMAINING: ({gameState.maxPoints})</div>
                    <div className="history-section">
                        <div className="history-label">HISTORY</div>
                        <div className="history-container">
                            <div className="player-history">
                                {gameState.player1PottedBalls.slice(-7).map((ball, index, arr) => (
                                    <div
                                        key={`p1-${index}`}
                                        className={`history-ball ${ball.toLowerCase()} ${(lastPotPlayer === 1 && index === arr.length - 1) ? 'last' : ''}`}
                                    >
                                        {(lastPotPlayer === 1 && index === arr.length - 1) && <div className="history-dot"></div>}
                                    </div>
                                ))}
                            </div>
                            <div className="player-history right">
                                {gameState.player2PottedBalls.slice(-7).reverse().map((ball, index) => (
                                    <div
                                        key={`p2-${index}`}
                                        className={`history-ball ${ball.toLowerCase()} ${(lastPotPlayer === 2 && index === 0) ? 'last' : ''}`}
                                    >
                                        {(lastPotPlayer === 2 && index === 0) && <div className="history-dot"></div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});
export default Snooker;
